/**
 * User Service - Abstracted user data management
 * Calls Rails API for all user operations
 */

import { createUser, validateUser, fromRailsFormat, toRailsFormat } from '../models/User.js'
import { apiRequest } from '../config/api.js'
import { handleApiError } from './errorHandler.js'

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} Current user object or null if not authenticated
 */
export async function getCurrentUser() {
  try {
    const data = await apiRequest('/users/current', {
      method: 'GET',
      includeAuth: true
    })
    return fromRailsFormat(data.user)
  } catch (error) {
    if (error.status === 401) {
      return null
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} User object or null if not found
 */
export async function getUserById(userId) {
  try {
    const data = await apiRequest(`/users/${userId}`, {
      method: 'GET',
      includeAuth: true
    })
    return fromRailsFormat(data.user)
  } catch (error) {
    if (error.status === 404) {
      return null
    }
    handleApiError(error)
    throw error
  }
}

/**
 * Update user
 * @param {string} userId - User ID
 * @param {Object} userData - Updated user data (in camelCase)
 * @returns {Promise<Object>} Updated user object
 */
export async function updateUser(userId, userData) {
  // Validate user data
  const userToValidate = createUser({ ...userData, id: userId })
  const validation = validateUser(userToValidate)
  if (!validation.isValid) {
    throw new Error(`Invalid user data: ${validation.errors.join(', ')}`)
  }

  // Convert to Rails format
  const railsData = toRailsFormat(userToValidate)
  // Remove id from update payload (sent in URL)
  delete railsData.id

  try {
    const data = await apiRequest(`/users/${userId}`, {
      method: 'PUT',
      includeAuth: true,
      body: JSON.stringify({ user: railsData })
    })
    return fromRailsFormat(data.user)
  } catch (error) {
    handleApiError(error)
    throw error
  }
}

